import { create } from 'zustand';
import {
  CustomSnippet,
  CreateCustomSnippetPayload,
  UpdateCustomSnippetPayload,
} from '@/types/api';
import {
  getCustomSnippets as apiGetCustomSnippets,
  createCustomSnippet as apiCreateCustomSnippet,
  updateCustomSnippet as apiUpdateCustomSnippet,
  deleteCustomSnippet as apiDeleteCustomSnippet,
  duplicateCustomSnippet as apiDuplicateCustomSnippet,
} from '@/lib/api';
import { toast } from 'sonner';

interface CustomSnippetState {
  snippets: CustomSnippet[];
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  editingSnippetId: string | 'new' | null;
  setEditingSnippetId: (id: string | 'new' | null) => void;
  fetchSnippets: () => Promise<void>;
  createSnippet: (payload: CreateCustomSnippetPayload) => Promise<CustomSnippet | null>;
  updateSnippet: (id: string, payload: UpdateCustomSnippetPayload) => Promise<CustomSnippet | null>;
  deleteSnippet: (id: string) => Promise<void>;
  duplicateSnippet: (id: string) => Promise<void>;
}

export const useCustomSnippetStore = create<CustomSnippetState>((set, get) => ({
  snippets: [],
  isLoading: false,
  isInitialized: false,
  error: null,
  editingSnippetId: null,
  setEditingSnippetId: (id) => set({ editingSnippetId: id }),

  fetchSnippets: async () => {
    // Skip if already initialized or currently loading (prevents duplicate fetch in Strict Mode)
    const { isInitialized, isLoading } = get();
    if (isInitialized || isLoading) return;
    
    set({ isLoading: true, error: null });
    try {
      const snippets = await apiGetCustomSnippets();
      set({ snippets, isLoading: false, isInitialized: true });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch custom snippets.';
      set({ error: errorMsg, isLoading: false });
      toast.error(errorMsg);
    }
  },

  createSnippet: async (payload) => {
    set({ isLoading: true, error: null });
    try {
      const newSnippet = await apiCreateCustomSnippet(payload);
      set((state) => ({
        snippets: [...state.snippets, newSnippet].sort((a, b) =>
          a.prefix.localeCompare(b.prefix)
        ),
        isLoading: false,
      }));
      toast.success('Custom snippet created successfully.');
      return newSnippet;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create custom snippet.';
      set({ error: errorMsg, isLoading: false });
      toast.error(errorMsg);
      return null;
    }
  },

  updateSnippet: async (id, payload) => {
    set({ isLoading: true, error: null });
    try {
      const updatedSnippet = await apiUpdateCustomSnippet(id, payload);
      set((state) => ({
        snippets: state.snippets
          .map((s) => (s.id === id ? updatedSnippet : s))
          .sort((a, b) => a.prefix.localeCompare(b.prefix)),
        isLoading: false,
      }));
      toast.success('Custom snippet updated successfully.');
      return updatedSnippet;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update custom snippet.';
      set({ error: errorMsg, isLoading: false });
      toast.error(errorMsg);
      return null;
    }
  },

  deleteSnippet: async (id: string) => {
    const originalSnippets = get().snippets;
    set((state) => ({
      snippets: state.snippets.filter((s) => s.id !== id),
      isLoading: true,
    }));
    try {
      await apiDeleteCustomSnippet(id);
      toast.success('Custom snippet deleted successfully.');
      set({ isLoading: false });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete custom snippet.';
      set({ error: errorMsg, snippets: originalSnippets, isLoading: false });
      toast.error(errorMsg);
    }
  },

  duplicateSnippet: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const duplicatedSnippet = await apiDuplicateCustomSnippet(id);
      set((state) => ({
        snippets: [...state.snippets, duplicatedSnippet].sort((a, b) =>
          a.prefix.localeCompare(b.prefix)
        ),
        isLoading: false,
      }));
      toast.success(`Snippet duplicated as "${duplicatedSnippet.prefix}".`);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to duplicate snippet.';
      set({ error: errorMsg, isLoading: false });
      toast.error(errorMsg);
    }
  },
}));